/**
 * Maps persisted workflow `node.type` strings to UI behaviour
 * (which drawer opens, which React Flow node component to use).
 */

export type TriggerEngineKind =
  | 'form_trigger'
  | 'webhook_trigger'
  | 'integration_trigger'
  | 'scheduled_trigger'
  | null;

export function triggerEngineKind(t: string): TriggerEngineKind {
  if (t === 'form_trigger') {
    return 'form_trigger';
  }
  if (t === 'webhook_trigger') {
    return 'webhook_trigger';
  }
  if (t === 'integration_trigger') {
    return 'integration_trigger';
  }
  if (t === 'scheduled_trigger') {
    return 'scheduled_trigger';
  }
  return null;
}

export const isFormTriggerEngine = (t: string): boolean =>
  t === 'trigger' || triggerEngineKind(t) === 'form_trigger';
export const isWebhookEngine = (t: string): boolean =>
  triggerEngineKind(t) === 'webhook_trigger';
export const isIntegrationEngine = (t: string): boolean =>
  triggerEngineKind(t) === 'integration_trigger';
export const isScheduleEngine = (t: string): boolean =>
  triggerEngineKind(t) === 'scheduled_trigger';
export const isTriggerEngine = (t: string): boolean =>
  triggerEngineKind(t) !== null;
export const isIntegrationActionEngine = (t: string): boolean =>
  t === 'integration';

export function isIfElseEngine(t: string): boolean {
  return t === 'if_else';
}

export function isModelEngine(t: string): boolean {
  return t === 'agent' || t === 'llm_call';
}

type TriggerFormField = {
  label?: string;
  /** Set on the auto-injected default upload field so it never counts as a
   * user-configured field. Applied at field creation. */
  isPlaceholder?: boolean;
};
export function formTriggerHasRequiredFields(
  config: Record<string, unknown> | undefined
): boolean {
  const fields = config?.fields;
  return (
    Array.isArray(fields) &&
    (fields as TriggerFormField[]).some(
      (f) => Boolean(f?.label?.trim()) && !f?.isPlaceholder
    )
  );
}

export function outputNodeValue(
  input: Record<string, unknown> | undefined,
  config: Record<string, unknown> | undefined
): string {
  const fromInput = input?.download_url;
  if (typeof fromInput === 'string') {
    return fromInput.trim();
  }
  const legacy = config?.format;
  if (typeof legacy === 'string' && legacy.trim() !== 'Workflow result') {
    return legacy.trim();
  }
  return '';
}
